import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, MapPin, Calendar, AlertTriangle,
  Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
  API_CACHE_TTL,
  cachedGet,
  readApiCache,
} from "../lib/apiCache";
import { useAuth } from "../lib/auth";
import { eventInviteUrl, eventPublicPath, eventEditPath } from "../lib/eventLinks";
import { buildEventWhatsAppSummary, copyWhatsAppSummary, openWhatsAppShare } from "../lib/share";
import {
  eventStatusColor,
  eventStatusLabel,
} from "../lib/eventStatus";
import { eventRequiresInvite } from "../lib/rsvp";
import { fmtDashboardDateTime } from "../lib/dashboardFormat";
import { ReadableCard, ReadableNavButton, Chip } from "../components/Sticker";
import HostInviteRequests from "../components/HostInviteRequests";
import DashboardLoadingSkeleton from "../components/dashboard/DashboardLoadingSkeleton";
import { TabButton } from "../components/ui";
import OccurrenceSelector from "../components/dashboard/OccurrenceSelector";
import EventDangerZone from "../components/dashboard/EventDangerZone";
import OverviewTab from "../components/dashboard/OverviewTab";
import DashboardShareActions from "../components/dashboard/DashboardShareActions";
import EditWeekTab from "../components/dashboard/EditWeekTab";
import NotifyTab from "../components/dashboard/NotifyTab";
import NotifyAfterSaveDialog from "../components/broadcasts/NotifyAfterSaveDialog";
import { defaultBackLabel, defaultBackPath, goBack } from "../lib/navBack";
import { dashboardListChanged, restoreWindowScroll } from "../lib/dashboardRefresh";
import { SM_UP_QUERY, useMediaQuery } from "../lib/useMediaQuery";

export default function DashboardPage() {
  const { eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const tokenParam = params.get("token");
  const occParam = params.get("occurrence_date");
  const nav = useNavigate();
  const location = useLocation();
  const { account } = useAuth();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [pastAttendees, setPastAttendees] = useState([]);
  const [updateSubscribers, setUpdateSubscribers] = useState([]);
  const [favouriteInsights, setFavouriteInsights] = useState(null);
  const [notifyAfterSaveOpen, setNotifyAfterSaveOpen] = useState(false);
  const [listRefreshNotice, setListRefreshNotice] = useState("");
  const isDesktop = useMediaQuery(SM_UP_QUERY);

  const dataRef = useRef(null);
  const loadRequestIdRef = useRef(0);
  const listRefreshNoticeTimerRef = useRef(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => () => {
    if (listRefreshNoticeTimerRef.current) {
      window.clearTimeout(listRefreshNoticeTimerRef.current);
    }
  }, []);

  const notifyListUpdated = useCallback(() => {
    toast("List updated", { duration: 2500 });
    setListRefreshNotice("Guest list updated.");
    if (listRefreshNoticeTimerRef.current) {
      window.clearTimeout(listRefreshNoticeTimerRef.current);
    }
    listRefreshNoticeTimerRef.current = window.setTimeout(() => {
      setListRefreshNotice("");
      listRefreshNoticeTimerRef.current = null;
    }, 4000);
  }, []);

  const loadDashboard = useCallback(async ({ background = false, force = false } = {}) => {
    const requestId = ++loadRequestIdRef.current;
    const scrollY = background ? window.scrollY : null;
    const previous = background ? dataRef.current : null;
    const queryParams = {
      token: tokenParam || undefined,
      occurrence_date: occParam || undefined,
    };
    const ttlMs = background
      ? API_CACHE_TTL.DASHBOARD_BACKGROUND
      : API_CACHE_TTL.DASHBOARD;

    try {
      if (!force && !background) {
        const peek = await readApiCache(`/events/${eventId}/dashboard`, queryParams);
        if (peek?.data) {
          setError(null);
          setData(peek.data);
        }
      }

      const { data: next } = await cachedGet(`/events/${eventId}/dashboard`, {
        params: queryParams,
        ttlMs,
        force,
      });
      if (requestId !== loadRequestIdRef.current) return;

      if (background && previous) {
        if (!dashboardListChanged(previous, next)) return;
        setData(next);
        restoreWindowScroll(scrollY);
        notifyListUpdated();
        return;
      }

      setError(null);
      setData(next);
    } catch (e) {
      if (requestId !== loadRequestIdRef.current) return;

      if (background && previous) {
        toast.error("Couldn't refresh the guest list. Showing what you had.");
        return;
      }
      setError(e?.response?.status === 403 ? "forbidden" : "not_found");
    }
  }, [eventId, tokenParam, occParam, notifyListUpdated]);

  const loadPast = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/past-attendees`, { params: { token: tokenParam || undefined } });
      setPastAttendees(r.data.attendees || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [eventId, tokenParam]);

  const loadUpdateSubscribers = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/update-notify-list`, {
        params: {
          token: tokenParam || undefined,
          occurrence_date: occParam || undefined,
        },
      });
      setUpdateSubscribers(r.data.subscribers || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [eventId, tokenParam, occParam]);

  useEffect(() => {
    setTabInitialized(false);
    setTab("overview");
    setListRefreshNotice("");
    setData(null);
    setError(null);
    loadRequestIdRef.current += 1;
  }, [eventId, occParam]);

  useEffect(() => {
    if (!data || tabInitialized) return;
    const pending = (data.invite_requests || []).length;
    if (eventRequiresInvite(data) && pending > 0) {
      setTab("approvals");
    }
    setTabInitialized(true);
  }, [data, tabInitialized]);

  const loadFavouriteInsights = useCallback(async () => {
    try {
      const r = await api.get(`/events/${eventId}/favourite-insights`, {
        params: {
          token: tokenParam || undefined,
          occurrence_date: occParam || undefined,
        },
      });
      setFavouriteInsights(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, [eventId, tokenParam, occParam]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!data?.recurring || data.recurring === "none") return;
    loadFavouriteInsights();
  }, [data?.id, data?.recurring, loadFavouriteInsights]);

  useEffect(() => {
    if (params.get("broadcast") !== "1" || !data) return;
    setTab("notify");
    const next = new URLSearchParams(params);
    next.delete("broadcast");
    setParams(next, { replace: true });
  }, [params, data, setParams]);

  useEffect(() => {
    function refreshOnVisible() {
      if (document.visibilityState !== "visible" || !dataRef.current) return;
      loadDashboard({ background: true });
    }
    document.addEventListener("visibilitychange", refreshOnVisible);
    return () => document.removeEventListener("visibilitychange", refreshOnVisible);
  }, [loadDashboard]);

  useEffect(() => {
    if (tab === "notify") {
      loadPast();
      loadUpdateSubscribers();
      loadFavouriteInsights();
    }
  }, [tab, loadPast, loadUpdateSubscribers, loadFavouriteInsights]);

  if (error === "forbidden") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas p-8 text-center">
        <ReadableCard className="p-7 max-w-md bg-pinky/40">
          <AlertTriangle className="w-8 h-8 mb-2" strokeWidth={2.5} />
          <p className="font-ui font-semibold text-[20px]">You don&apos;t host this event</p>
          <p className="font-ui font-medium text-[15px] mt-2 text-ink/80">
            Sign in with the host account, or open the dashboard link from your original event email or WhatsApp thread.
          </p>
          <div className="mt-4 flex gap-2 flex-wrap justify-center">
            <ReadableNavButton as={Link} to={`/login?next=/dashboard/${eventId}`} variant="primary">
              Sign in as host
            </ReadableNavButton>
            <ReadableNavButton as={Link} to="/">
              Back home
            </ReadableNavButton>
          </div>
        </ReadableCard>
      </div>
    );
  }
  if (error === "not_found") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-canvas p-8 text-center">
        <p className="font-ui font-semibold text-[20px]">Event not found</p>
        <p className="font-ui font-medium text-[15px] text-ink/70 mt-2 max-w-sm">
          The link may be wrong, or this event was deleted. Check the URL or go back to your events.
        </p>
        <ReadableNavButton as={Link} to="/events" className="mt-4">
          My events
        </ReadableNavButton>
      </div>
    );
  }
  if (!data) {
    return <DashboardLoadingSkeleton />;
  }

  const counts = data.counts;
  const L = counts.balanced_leaders;
  const F = counts.balanced_followers;
  const total = L + F;
  const lpct = total > 0 ? (L / total) * 100 : 50;
  const cap = data.capacity;
  const link = eventInviteUrl(data);
  const isRecurring = data.recurring && data.recurring !== "none";
  const requiresInvite = eventRequiresInvite(data);
  const pendingInvites = (data.invite_requests || []).length;
  const isArchived = data.lifecycle === "archived";

  const shareText = buildEventWhatsAppSummary(data, { link, counts });

  function copyLink() {
    navigator.clipboard.writeText(link);
    toast.success("Invite link copied — paste it in WhatsApp or your group chat.");
  }
  function shareEvent() {
    openWhatsAppShare(shareText);
  }
  async function copyHypeBlurb() {
    await copyWhatsAppSummary(shareText);
    toast.success("Share text copied — paste it in WhatsApp.");
  }

  function changeOccurrence(occKey) {
    const next = new URLSearchParams(params);
    if (occKey) next.set("occurrence_date", occKey);
    else next.delete("occurrence_date");
    setParams(next, { replace: true });
  }

  function handleDashboardTabKeyDown(e) {
    const navKeys = ["ArrowLeft", "ArrowRight", "Home", "End"];
    if (!navKeys.includes(e.key)) return;

    const tabs = [...e.currentTarget.querySelectorAll('[role="tab"]')];
    if (tabs.length === 0) return;

    const currentIndex = tabs.indexOf(document.activeElement);
    if (currentIndex === -1 && e.key !== "Home" && e.key !== "End") return;

    e.preventDefault();

    let nextIndex = currentIndex;
    if (e.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else if (e.key === "End") {
      nextIndex = tabs.length - 1;
    }

    const nextTab = tabs[nextIndex];
    const tabKey = nextTab?.dataset?.tabKey;
    if (!tabKey) return;

    nextTab.focus();
    setTab(tabKey);
  }

  return (
    <div
      className="min-h-screen bg-canvas font-body text-ink pb-6"
    >
      <div className="max-w-page mx-auto px-4 pt-5 pb-2 flex items-center justify-between flex-wrap gap-2">
        <ReadableNavButton
          type="button"
          onClick={() => goBack(nav, location, [defaultBackPath(account)])}
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
          {location.state?.from ? "Back" : account ? "My events" : defaultBackLabel(account)}
        </ReadableNavButton>
        <div className="flex items-center gap-2 flex-wrap">
          {isDesktop && (
            <DashboardShareActions
              placement="inline"
              shareEvent={shareEvent}
              copyLink={copyLink}
              copyHypeBlurb={copyHypeBlurb}
            />
          )}
          <EventDangerZone
            eventId={eventId}
            tokenParam={tokenParam}
            lifecycle={data.lifecycle}
            rsvpCount={counts.total}
            isRecurring={isRecurring}
            occurrenceDate={data.selected_occurrence}
            eventStatus={data.event_status}
            nav={nav}
            onUpdated={() => loadDashboard({ force: true })}
            editHref={eventEditPath(data)}
            viewPageHref={eventPublicPath(data) || "/events"}
          />
        </div>
      </div>

      <div className="max-w-page mx-auto px-4 mt-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1
            className="font-display font-medium text-[32px] sm:text-[40px] leading-tight text-balance"
            data-testid="dashboard-event-name"
          >
            {data.name}
          </h1>
          {isRecurring && <Chip color="purple"><Repeat className="w-3 h-3" strokeWidth={2.5} /> {data.recurring}</Chip>}
        </div>
        <p className="mt-1 font-ui font-medium text-[14px] text-ink/70 inline-flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" strokeWidth={2.5} /> {fmtDashboardDateTime(data.starts_at)}</span>
          <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" strokeWidth={2.5} /> {data.location}{data.city ? `, ${data.city}` : ""}</span>
          {data.event_status && data.event_status !== "confirmed" && (
            <Chip color={eventStatusColor(data.event_status)}>
              {eventStatusLabel(data.event_status)}
              {data.status_reason ? ` · ${data.status_reason}` : ""}
            </Chip>
          )}
        </p>
      </div>

      {isArchived && (
        <div className="max-w-page mx-auto px-4 mt-4">
          <p className="font-ui font-medium text-[14px] text-ink/80 bg-cream border border-black/[0.08] rounded-card px-4 py-3" data-testid="archived-banner">
            Archived — guests can&apos;t RSVP or open the public page. Restore or delete it from More.
          </p>
        </div>
      )}

      {isRecurring && data.occurrences?.length > 1 && (
        <OccurrenceSelector
          occurrences={data.occurrences}
          selected={data.selected_occurrence}
          onChange={changeOccurrence}
        />
      )}

      <div className="max-w-page mx-auto px-4 mt-6">
        <div
          className="inline-flex gap-2 flex-wrap"
          role="tablist"
          aria-label="Dashboard sections"
          data-testid="dashboard-tabs"
          onKeyDown={handleDashboardTabKeyDown}
        >
          <TabButton tabKey="overview" active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview" panelId="dashboard-panel-overview">Overview</TabButton>
          {requiresInvite && (
            <TabButton
              tabKey="approvals"
              active={tab === "approvals"}
              onClick={() => setTab("approvals")}
              testid="tab-approvals"
              panelId="dashboard-panel-approvals"
              badge={pendingInvites > 0 ? pendingInvites : null}
            >
              Approvals
            </TabButton>
          )}
          {isRecurring && (
            <TabButton tabKey="edit" active={tab === "edit"} onClick={() => setTab("edit")} testid="tab-edit" panelId="dashboard-panel-edit">Edit week</TabButton>
          )}
          <TabButton tabKey="notify" active={tab === "notify"} onClick={() => setTab("notify")} testid="tab-notify" panelId="dashboard-panel-notify">Reach out</TabButton>
        </div>
      </div>

      {tab === "overview" && (
        <div role="tabpanel" id="dashboard-panel-overview" aria-labelledby="tab-overview">
          <OverviewTab
            key={`${eventId}-${data.selected_occurrence || ""}`}
            data={data} counts={counts} L={L} F={F} lpct={lpct} cap={cap}
            copyLink={copyLink} shareEvent={shareEvent} copyHypeBlurb={copyHypeBlurb}
            eventId={eventId} tokenParam={tokenParam} occurrenceDate={data.selected_occurrence}
            eventName={data.name}
            account={account}
            onRsvpRemoved={() => loadDashboard({ force: true })}
            listRefreshNotice={listRefreshNotice}
            favouriteInsights={favouriteInsights}
            showShareActions={!isDesktop}
          />
        </div>
      )}
      {tab === "approvals" && requiresInvite && (
        <div role="tabpanel" id="dashboard-panel-approvals" aria-labelledby="tab-approvals" className="max-w-page mx-auto px-4 mt-6" data-testid="host-invite-requests">
          <HostInviteRequests
            requests={data.invite_requests || []}
            eventId={eventId}
            eventName={data.name}
            hostName={data.organizer_name || account?.name || ""}
            schoolName={data.host_school?.name || data.school_external_name || ""}
            isPaid={data.pricing?.type && data.pricing.type !== "free"}
            tokenParam={tokenParam}
            onUpdated={() => loadDashboard({ force: true })}
          />
        </div>
      )}
      {tab === "edit" && isRecurring && (
        <div role="tabpanel" id="dashboard-panel-edit" aria-labelledby="tab-edit">
          <EditWeekTab
            data={data}
            eventId={eventId}
            tokenParam={tokenParam}
            occurrenceDate={data.selected_occurrence}
            onSaved={() => loadDashboard({ force: true })}
            onNotifyAfterSave={() => setNotifyAfterSaveOpen(true)}
          />
        </div>
      )}
      {tab === "notify" && (
        <div role="tabpanel" id="dashboard-panel-notify" aria-labelledby="tab-notify">
          <NotifyTab
            attendees={pastAttendees}
            updateSubscribers={updateSubscribers}
            repeatWithoutFavourite={favouriteInsights?.repeat_without_favourite || []}
            eventId={eventId}
            occurrenceDate={data?.selected_occurrence}
            tokenParam={tokenParam}
            rsvpCount={counts.total}
          />
        </div>
      )}

      <NotifyAfterSaveDialog
        open={notifyAfterSaveOpen}
        onYes={() => {
          setNotifyAfterSaveOpen(false);
          setTab("notify");
        }}
        onNo={() => setNotifyAfterSaveOpen(false)}
      />
    </div>
  );
}
